/**
 * Busca de locais + geolocalização atual para o formulário de localização do chat.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    PlaceResult,
    cleanPlaceAddress,
    reverseGeocode,
    searchNearbyPlaces,
    searchPlaces,
} from '../lib/locationPlaces';
import { notify } from '../../../utils/toast';

const CURRENT_NAME = 'Minha localização';
const GEO_OPTS: PositionOptions = { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 };

function withCleanAddress(place: PlaceResult): PlaceResult {
    return {
        ...place,
        address: cleanPlaceAddress(place),
    };
}

function asCurrentPlace(place: PlaceResult): PlaceResult {
    return withCleanAddress({ ...place, id: 'current', name: CURRENT_NAME });
}

export function useLocationPlaceSearch(isOpen: boolean) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<PlaceResult[]>([]);
    const [nearby, setNearby] = useState<PlaceResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingNearby, setIsLoadingNearby] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [currentPlace, setCurrentPlace] = useState<PlaceResult | null>(null);
    const [userCoords, setUserCoords] = useState<{ latitude: number; longitude: number } | null>(null);
    const debounceRef = useRef<number | null>(null);
    const requestIdRef = useRef(0);
    const nearbyReqRef = useRef(0);
    const reverseReadyRef = useRef(false);

    const reset = useCallback(() => {
        setQuery('');
        setResults([]);
        setNearby([]);
        setIsSearching(false);
        setIsLoadingNearby(false);
        setIsLocating(false);
        setCurrentPlace(null);
        setUserCoords(null);
        reverseReadyRef.current = false;
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
    }, []);

    useEffect(() => {
        if (!isOpen) reset();
    }, [isOpen, reset]);

    const locateMe = useCallback(() => {
        if (!navigator.geolocation) {
            notify.error('Geolocalização não é suportada neste navegador.');
            return;
        }
        setIsLocating(true);
        reverseReadyRef.current = false;
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                setUserCoords({ latitude, longitude });
                try {
                    const place = await reverseGeocode(latitude, longitude);
                    reverseReadyRef.current = true;
                    setCurrentPlace(asCurrentPlace(place));
                } catch {
                    reverseReadyRef.current = true;
                    setCurrentPlace({
                        id: 'current',
                        name: CURRENT_NAME,
                        address: CURRENT_NAME,
                        latitude,
                        longitude,
                    });
                } finally {
                    setIsLocating(false);
                }
            },
            () => {
                notify.error('Não foi possível obter sua localização. Permita o acesso ou busque um local.');
                setIsLocating(false);
            },
            GEO_OPTS,
        );
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        locateMe();
    }, [isOpen, locateMe]);

    const isSearchMode = query.trim().length >= 2;

    // Locais próximos — só fora do modo busca. Não promove POI a "atual".
    useEffect(() => {
        if (!isOpen || !userCoords || isSearchMode) return;

        const reqId = ++nearbyReqRef.current;
        let cancelled = false;
        const isLatest = () => !cancelled && reqId === nearbyReqRef.current;
        setIsLoadingNearby(true);

        void (async () => {
            try {
                const places = await searchNearbyPlaces(userCoords);
                if (!isLatest()) return;
                setNearby(places);

                if (!reverseReadyRef.current) {
                    try {
                        const place = await reverseGeocode(userCoords.latitude, userCoords.longitude);
                        reverseReadyRef.current = true;
                        setCurrentPlace(asCurrentPlace(place));
                    } catch {
                        /* silencioso — usuário ainda pode buscar */
                    }
                }
            } catch {
                if (isLatest()) {
                    setNearby([]);
                    notify.info('Não foi possível carregar locais próximos. Busque pelo nome.');
                }
            } finally {
                if (isLatest()) setIsLoadingNearby(false);
            }
        })();

        const safetyTimer = globalThis.setTimeout(() => {
            if (isLatest()) setIsLoadingNearby(false);
        }, 16000);

        return () => {
            cancelled = true;
            globalThis.clearTimeout(safetyTimer);
        };
    }, [isOpen, userCoords, isSearchMode]);

    useEffect(() => {
        if (!isOpen) return;
        if (debounceRef.current) window.clearTimeout(debounceRef.current);

        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        setResults([]);
        setIsSearching(true);
        const reqId = ++requestIdRef.current;
        debounceRef.current = window.setTimeout(async () => {
            const isLatest = () => reqId === requestIdRef.current;
            try {
                const places = await searchPlaces(q, userCoords);
                if (isLatest()) setResults(places);
            } catch (err) {
                if (!isLatest()) return;
                setResults([]);
                const msg = err instanceof Error ? err.message : '';
                if (/Google Places|403|PERMISSION|Billing|REQUEST_DENIED/i.test(msg)) {
                    notify.error(
                        'Busca Google indisponível. Confira a chave, faturamento e APIs no Cloud.',
                    );
                } else {
                    notify.info('Não foi possível buscar locais agora. Tente de novo.');
                }
            } finally {
                if (isLatest()) setIsSearching(false);
            }
        }, 550);

        return () => {
            if (debounceRef.current) window.clearTimeout(debounceRef.current);
        };
    }, [query, userCoords, isOpen]);

    return {
        query,
        setQuery,
        results: isSearchMode ? results : nearby,
        isSearching: isSearchMode ? isSearching : isLoadingNearby,
        isSearchMode,
        isLocating,
        currentPlace,
        locateMe,
        reset,
    };
}
